import { heicTo } from "heic-to";
import { imageDimensionsFromData } from "image-dimensions";
import mime from "mime-types";

/** Bytes to read from the start of a file for dimension probes (enough for HEIF meta). */
const IMAGE_HEADER_PROBE_BYTES = 512 * 1024;

/**
 * If the file is HEIC/HEIF (by MIME or extension fallback), convert to JPEG for upload.
 * Otherwise returns the original file reference.
 */
export function isHeicOrHeifFile(file: File): boolean {
  const effectiveType = file.type || mime.lookup(file.name) || "";
  return effectiveType === "image/heic" || effectiveType === "image/heif";
}

export function getHeicJpegFileName(fileName: string): string {
  const converted = fileName.replace(/\.(heic|heif)$/i, ".jpg");
  return converted === fileName ? `${fileName}.jpg` : converted;
}

export function getPlannedUploadFile(file: File): {
  name: string;
  contentType: string;
  willConvertHeic: boolean;
} {
  if (isHeicOrHeifFile(file)) {
    return {
      name: getHeicJpegFileName(file.name),
      contentType: "image/jpeg",
      willConvertHeic: true,
    };
  }
  return {
    name: file.name,
    contentType: file.type || mime.lookup(file.name) || "",
    willConvertHeic: false,
  };
}

export async function convertHeicToJpegIfNeeded(file: File): Promise<File> {
  const effectiveType = file.type || mime.lookup(file.name) || "";
  if (effectiveType !== "image/heic" && effectiveType !== "image/heif") {
    return file;
  }

  let jpegBlob: Blob;
  try {
    jpegBlob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.75,
    });
  } catch (cause) {
    // Surface a clear, typed error so the upload queue's onUploadError
    // toast/Sentry capture is meaningful. We deliberately do NOT fall back
    // to uploading the raw HEIC — that would silently produce undisplayable
    // images downstream.
    throw new Error(`Could not convert HEIC image "${file.name}" to JPEG.`, {
      cause,
    });
  }

  return new File([jpegBlob], getHeicJpegFileName(file.name), {
    type: "image/jpeg",
  });
}

/**
 * Decode natural pixel size via full image decode. Returns null if decode fails.
 */
export async function readImageBitmapDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const width = bitmap.width;
    const height = bitmap.height;
    bitmap.close();
    if (width <= 0 || height <= 0) {
      return null;
    }
    return { width, height };
  } catch {
    return null;
  }
}

/**
 * Read stored pixel dimensions from image headers (JPEG, PNG, GIF, WebP, AVIF, HEIC, …)
 * using `image-dimensions`, without full decode. Falls back to `createImageBitmap` when
 * the format is unsupported or the probe slice is insufficient.
 */
export async function readImageFileDimensions(
  file: File,
): Promise<{ width: number; height: number } | null> {
  try {
    const byteLength = Math.min(file.size, IMAGE_HEADER_PROBE_BYTES);
    if (byteLength > 0) {
      const buffer = await file.slice(0, byteLength).arrayBuffer();
      const parsed = imageDimensionsFromData(new Uint8Array(buffer), {
        resolveOrientation: true,
      });
      if (parsed && parsed.width > 0 && parsed.height > 0) {
        return { width: parsed.width, height: parsed.height };
      }
    }
  } catch {
    // fall through to decode
  }
  return readImageBitmapDimensions(file);
}
